// Supabase implementation of TrainingApi: the RPCs of 20261014140000_gavion_training.sql. Field missions come from
// the server (my_training_status computes them); the client never decides them.
import type { SupabaseClient } from "@supabase/supabase-js";
import {
  FIELD_MISSION_KEYS,
  isPlayableKey,
  type PlayableKey,
} from "../../domain/training/missions";
import {
  fieldMissionsFrom,
  type FieldStats,
  type ProgressRow,
  type TrainingStatus,
} from "../../domain/training/progress";
import {
  TrainingApiError,
  type TrainingApi,
  type TrainingErrorCode,
} from "./types";

type Json = Record<string, unknown>;
const KNOWN: readonly TrainingErrorCode[] = [
  "forbidden",
  "invalid_mission",
  "invalid_score",
];

const fail = (error: { message?: string } | null): never => {
  const code = KNOWN.find((c) => c === error?.message) ?? "unknown";
  throw new TrainingApiError(code);
};

const num = (v: unknown): number =>
  typeof v === "number" ? v : Number(v ?? 0) || 0;
const strOrNull = (v: unknown): string | null =>
  typeof v === "string" && v !== "" ? v : null;

export const parseProgressRow = (r: Json): ProgressRow | null =>
  isPlayableKey(String(r.mission_key))
    ? {
        mission_key: r.mission_key as PlayableKey,
        completed_at: strOrNull(r.completed_at),
        best_score: num(r.best_score),
        attempts: num(r.attempts),
        xp: num(r.xp),
        updated_at: strOrNull(r.updated_at),
      }
    : null;

/** jsonb from private.training_status_for → TrainingStatus (missing parts default to « nothing yet »). */
export function parseStatus(raw: unknown): TrainingStatus {
  const o = (raw && typeof raw === "object" ? raw : {}) as Json;
  const s = (o.stats ?? {}) as Json;
  const stats: FieldStats = {
    softphone_verified: s.softphone_verified === true,
    sessions: num(s.sessions),
    logged_calls: num(s.logged_calls),
    reached_human: num(s.reached_human),
    callbacks: num(s.callbacks),
    demos: num(s.demos),
    contact_calls: num(s.contact_calls),
    unlogged: num(s.unlogged),
  };
  const m = (o.missions ?? {}) as Json;
  const computed = fieldMissionsFrom(stats);
  return {
    sales_id: num(o.sales_id),
    first_name: strOrNull(o.first_name),
    last_name: strOrNull(o.last_name),
    progress: (Array.isArray(o.progress) ? (o.progress as Json[]) : [])
      .map(parseProgressRow)
      .filter((r): r is ProgressRow => r !== null),
    stats,
    // the server's flags; a missing flag falls back to the same rule on the server's counts
    missions: Object.fromEntries(
      FIELD_MISSION_KEYS.map((k) => [
        k,
        typeof m[k] === "boolean" ? (m[k] as boolean) : computed[k],
      ]),
    ) as TrainingStatus["missions"],
    last_activity: strOrNull(o.last_activity),
  };
}

/** `sb` may be a getter: the client is then created on the first call. */
export function createSupabaseTrainingApi(
  sb: SupabaseClient | (() => SupabaseClient),
): TrainingApi {
  const db = (): SupabaseClient => (typeof sb === "function" ? sb() : sb);
  const rpc = async (fn: string, args?: Json): Promise<unknown> => {
    const { data, error } = await db().rpc(fn, args);
    if (error) fail(error);
    return data;
  };
  return {
    async myStatus() {
      return parseStatus(await rpc("my_training_status"));
    },
    async teamStatus() {
      const data = await rpc("team_training_status");
      return (Array.isArray(data) ? data : []).map(parseStatus);
    },
    async recordAttempt(key, score) {
      const row = parseProgressRow(
        ((await rpc("record_training_attempt", {
          p_mission_key: key,
          p_score: score,
        })) ?? {}) as Json,
      );
      if (!row) throw new TrainingApiError("unknown");
      return row;
    },
  };
}
